import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SidebarProvider, Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { LayoutDashboard, FileText, Plus, ArrowLeft, LogOut, User, Clock, CheckCircle, XCircle, Eye } from "lucide-react";
import { getVolunteerProfile, getVolunteerArticles, logoutVolunteer } from "@/lib/api";

interface VolunteerArticle {
  id: string;
  title: string;
  excerpt: string;
  status: 'pending' | 'approved' | 'rejected';
  verification_status: 'pending' | 'approved' | 'rejected';
  verification_notes?: string;
  created_at: string;
  view_count: number;
}

const VolunteerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<VolunteerArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [volunteerName, setVolunteerName] = useState('Relawan');
  const [token, setToken] = useState<string | null>(null);

  // Load volunteer data and articles
  useEffect(() => {
    const loadData = async () => {
      const storedToken = localStorage.getItem('volunteer_token');

      if (!storedToken) {
        navigate('/volunteer/login');
        return;
      }

      setToken(storedToken);
      setLoading(true);
      setError(null);

      try {
        // Get volunteer profile
        const profile = await getVolunteerProfile(storedToken);
        if (profile.name) {
          setVolunteerName(profile.name);
          localStorage.setItem('volunteer_name', profile.name);
        }

        // Get volunteer articles
        const articlesData = await getVolunteerArticles(storedToken);
        setArticles(articlesData);

      } catch (error: any) {
        console.error('Error loading volunteer data:', error);
        setError(error.message || 'Failed to load dashboard data');

        // If unauthorized, redirect to login
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          localStorage.removeItem('volunteer_token');
          localStorage.removeItem('volunteer_name');
          navigate('/volunteer/login');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      if (token) {
        await logoutVolunteer(token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('volunteer_token');
      localStorage.removeItem('volunteer_name');
      navigate('/volunteer/login');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };

    const labels = {
      pending: "Menunggu Verifikasi",
      approved: "Disetujui",
      rejected: "Ditolak",
    };

    const icons = {
      pending: <Clock className="h-3 w-3 mr-1" />,
      approved: <CheckCircle className="h-3 w-3 mr-1" />,
      rejected: <XCircle className="h-3 w-3 mr-1" />,
    };

    return (
      <Badge className={styles[status as keyof typeof styles]}>
        {icons[status as keyof typeof icons]}
        {labels[status as keyof typeof labels]}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const stats = {
    total: articles.length,
    pending: articles.filter(a => a.verification_status === 'pending').length,
    approved: articles.filter(a => a.verification_status === 'approved').length,
    rejected: articles.filter(a => a.verification_status === 'rejected').length,
    totalViews: articles.reduce((sum, a) => sum + a.view_count, 0),
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        {/* Sidebar */}
        <Sidebar>
          <SidebarContent className="p-4">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-blue-600">MindfulMe</h2>
              <p className="text-sm text-gray-600">Dashboard Relawan</p>
            </div>

            <SidebarGroup className="space-y-2">
              <SidebarGroupLabel className="text-sm font-semibold text-gray-600 mb-4">
                Menu
              </SidebarGroupLabel>

              <SidebarGroupContent>
                <SidebarMenu className="space-y-2">
                  <SidebarMenuItem>
                    <SidebarMenuButton className="w-full bg-blue-50 text-blue-600">
                      <LayoutDashboard className="h-5 w-5 mr-3" />
                      <span className="font-medium">Dashboard</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className="w-full hover:bg-gray-100"
                      onClick={() => navigate('/volunteer/submit')}
                    >
                      <Plus className="h-5 w-5 mr-3" />
                      <span className="font-medium">Kirim Artikel Baru</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <div className="mt-8 pt-8 border-t">
              <div className="flex items-center gap-2 mb-4 px-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">{volunteerName}</p>
                  <p className="text-xs text-gray-500">Relawan</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="w-full"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Keluar
              </Button>
            </div>
          </SidebarContent>
        </Sidebar>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">
                Dashboard Relawan
              </h1>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Kembali ke Beranda
              </Button>
            </div>
          </div>

          {/* Content */}
          <main className="flex-1 p-6 space-y-6">
            {/* Welcome Message */}
            <Alert className="border-blue-200 bg-blue-50">
              <AlertDescription className="text-blue-800">
                <strong>Selamat datang, {volunteerName}!</strong> Terima kasih telah berkontribusi sebagai relawan MindfulMe.
              </AlertDescription>
            </Alert>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Artikel</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <p className="text-xs text-muted-foreground">
                    Semua artikel yang Anda kirim
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Menunggu Verifikasi</CardTitle>
                  <Clock className="h-4 w-4 text-yellow-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                  <p className="text-xs text-muted-foreground">
                    Sedang ditinjau admin
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Disetujui</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
                  <p className="text-xs text-muted-foreground">
                    Artikel telah dipublikasikan
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Views</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalViews}</div>
                  <p className="text-xs text-muted-foreground">
                    Total pembaca artikel
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* My Articles */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Artikel Saya</CardTitle>
                  <CardDescription>
                    Daftar artikel yang telah Anda kirimkan
                  </CardDescription>
                </div>
                <Button onClick={() => navigate('/volunteer/submit')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Artikel Baru
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : error ? (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : articles.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Belum ada artikel</h3>
                    <p className="text-gray-500 mb-4">
                      Mulai kontribusi dengan mengirim artikel pertama Anda
                    </p>
                    <Button onClick={() => navigate('/volunteer/submit')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Kirim Artikel
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {articles.map((article) => (
                      <div key={article.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-1">
                              {article.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {article.excerpt}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>Dikirim: {formatDate(article.created_at)}</span>
                              {article.view_count > 0 && (
                                <span>Dibaca: {article.view_count} kali</span>
                              )}
                            </div>
                          </div>
                          {getStatusBadge(article.verification_status)}
                        </div>
                        {article.verification_notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                            <span className="font-medium">Catatan: </span>
                            {article.verification_notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default VolunteerDashboard;